/**
 * Headless props for the personal overview card on a past-challenge detail:
 * the one place everything user-specific about a finished challenge is
 * condensed. The v1 card renders the `primary` items (steps, rank, longest
 * streak); the `secondary` items carry the rest of the contract so widening
 * the card later is rendering work only.
 */
import { ordinal } from "./challenge-standing.js";
import { pastChallengeDateRange } from "./past-challenge-card.js";
export function pastChallengeOverviewProps(stats, options) {
    const primary = [
        { key: "steps", label: "Your steps", value: stats.steps.toLocaleString() },
    ];
    if (stats.rank !== null) {
        primary.push({
            key: "rank",
            label: "Final rank",
            value: `${ordinal(stats.rank)} of ${options.participantCount}`,
        });
    }
    if (stats.longestStreakDays > 0) {
        primary.push({
            key: "streak",
            label: "Longest streak",
            value: `${stats.longestStreakDays} ${stats.longestStreakDays === 1 ? "day" : "days"}`,
        });
    }
    const secondary = [];
    if (stats.distanceMeters > 0) {
        secondary.push({
            key: "distance",
            label: "Distance",
            value: `${(stats.distanceMeters / 1000).toFixed(1)} km`,
        });
    }
    if (stats.bestDaySteps > 0) {
        secondary.push({
            key: "bestDay",
            label: "Best day",
            value: `${stats.bestDaySteps.toLocaleString()} steps`,
        });
    }
    if (stats.activeDays > 0) {
        secondary.push({
            key: "activeDays",
            label: "Active days",
            value: `${stats.activeDays}`,
        });
    }
    if (stats.milestonesReached > 0) {
        secondary.push({
            key: "milestones",
            label: "Waypoints reached",
            value: `${stats.milestonesReached}`,
        });
    }
    return {
        primary,
        secondary,
        finishedRoute: stats.finishedRoute,
        routeOutcomeLine: routeOutcomeLine(stats.progressFraction, stats.finishedRoute),
    };
}
/**
 * The timeline endpoint returns frames from the earliest enrollment through
 * today; a past challenge replays only its own window, so the flat months
 * after the finish never drag the playback out.
 */
export function pastChallengeTimelineFrames(frames, window) {
    return (frames ?? []).filter((frame) => frame.date >= window.startsOn && frame.date <= window.endsOn);
}
/**
 * The per-member dismissal key for one challenge's completion announcement,
 * stored through the explainer-dismissals seam so the record follows the
 * member, not the browser. A second account on the same machine still gets
 * its own announcement, and the member's own dismissal holds on every device.
 */
export function pastChallengeCompletedDismissalKey(challengeId) {
    return `healthos.past-challenge.completed.v1:${challengeId.toLowerCase()}`;
}
function routeOutcomeLine(progressFraction, finishedRoute) {
    if (finishedRoute)
        return "You finished the route";
    const percent = Math.round(Math.min(1, progressFraction) * 100);
    return `You covered ${percent}% of the route`;
}
export function pastChallengeOverviewCardProps(summary, viewerStats) {
    const days = inclusiveDays(summary.startsOn, summary.endsOn);
    const { viewer } = summary;
    const dailyAverage = viewer.steps > 0 && days > 0 ? Math.round(viewer.steps / days) : null;
    const streakDays = viewerStats?.longestStreakDays ?? viewer.longestStreakDays;
    const activeDays = viewerStats?.activeDays ?? viewer.activeDays;
    const bestDaySteps = viewerStats?.bestDaySteps ?? viewer.bestDaySteps;
    const routePercent = Math.round(Math.min(1, viewer.progressFraction) * 100);
    const gapPart = viewer.rank === 1
        ? "You placed first"
        : viewer.rank !== null && viewer.stepsBehindNext !== null && viewer.stepsBehindNext > 0
            ? `${viewer.stepsBehindNext.toLocaleString()} steps behind ${ordinal(viewer.rank - 1)} place`
            : null;
    return {
        challengeId: summary.id,
        endedLine: `${pastChallengeDateRange(summary.startsOn, summary.endsOn)} · ${summary.routeName}`,
        participantLabel: `${summary.participantCount} ${summary.participantCount === 1 ? "participant" : "participants"}`,
        avatars: summary.participantsPreview,
        extraParticipants: Math.max(0, summary.participantCount - summary.participantsPreview.length),
        participantCount: summary.participantCount,
        headline: viewer.rank !== null ? `You finished ${ordinal(viewer.rank)}` : "You took part",
        subline: gapPart ?? "",
        routeLabel: viewer.finishedRoute ? "Route" : "Route progress",
        routeValue: viewer.finishedRoute ? "Complete" : `${routePercent}%`,
        steps: viewer.steps,
        stepsLabel: viewer.steps > 0 ? viewer.steps.toLocaleString() : null,
        dailyAverageLabel: dailyAverage !== null ? dailyAverage.toLocaleString() : null,
        distanceMeters: viewer.distanceMeters,
        activeDaysLabel: activeDays > 0 ? `${activeDays} of ${days}` : null,
        bestDayLabel: bestDaySteps > 0 ? bestDaySteps.toLocaleString() : null,
        streakLabel: streakDays > 0 ? `${streakDays} ${streakDays === 1 ? "day" : "days"}` : null,
        standings: cardStandings(summary),
    };
}
/**
 * The tile row every past-challenge surface shares, in a fixed order, with
 * the tiles that have nothing honest to say left out. Distance is the one
 * value the member's unit preference decides, so the caller formats it.
 */
export function pastChallengeOverviewTiles(card, formatDistanceValue) {
    const tiles = [
        { key: "route", label: card.routeLabel, value: card.routeValue },
    ];
    if (card.dailyAverageLabel) {
        tiles.push({ key: "average", label: "Daily average", value: card.dailyAverageLabel });
    }
    if (card.distanceMeters > 0) {
        tiles.push({
            key: "distance",
            label: "Distance",
            value: formatDistanceValue(card.distanceMeters),
        });
    }
    if (card.bestDayLabel) {
        tiles.push({ key: "bestDay", label: "Best day", value: card.bestDayLabel });
    }
    if (card.activeDaysLabel) {
        tiles.push({ key: "activeDays", label: "Active days", value: card.activeDaysLabel });
    }
    if (card.streakLabel) {
        tiles.push({ key: "streak", label: "Longest streak", value: card.streakLabel });
    }
    return tiles;
}
function cardStandings(summary) {
    const viewerId = summary.viewerIdentity?.userId ?? null;
    const rows = summary.topThree.map((entry) => ({
        userId: entry.userId,
        rank: entry.rank,
        displayName: entry.displayName,
        initials: entry.initials,
        avatarUrl: entry.avatarUrl,
        avatarColor: entry.avatarColor,
        steps: entry.score,
        isViewer: entry.userId === viewerId,
    }));
    const { viewer } = summary;
    if (viewer.rank !== null &&
        summary.viewerIdentity &&
        !rows.some((row) => row.userId === viewerId)) {
        rows.push({
            userId: summary.viewerIdentity.userId,
            rank: viewer.rank,
            displayName: summary.viewerIdentity.displayName,
            initials: summary.viewerIdentity.initials,
            avatarUrl: summary.viewerIdentity.avatarUrl,
            avatarColor: summary.viewerIdentity.avatarColor,
            steps: viewer.steps,
            isViewer: true,
        });
    }
    const leader = rows.reduce((max, row) => Math.max(max, row.steps), 0);
    return rows.map((row) => ({
        ...row,
        fraction: leader > 0 ? row.steps / leader : 0,
    }));
}
function inclusiveDays(startsOn, endsOn) {
    const utc = (dateKey) => {
        const [year = 0, month = 1, day = 1] = dateKey.split("-").map(Number);
        return Date.UTC(year, month - 1, day);
    };
    return Math.max(1, Math.round((utc(endsOn) - utc(startsOn)) / 86_400_000) + 1);
}
export function pastChallengeCompanyOutcomeTiles(stats, formatDistanceValue) {
    const walked = stats.totalSteps > 0;
    const targetPercent = Math.round((stats.totalSteps / Math.max(1, stats.targetSteps)) * 100);
    const tiles = [
        walked
            ? { key: "steps", label: "Steps together", value: stats.totalSteps.toLocaleString() }
            : null,
        walked && stats.totalMeters > 0
            ? {
                key: "distance",
                label: "Distance together",
                value: formatDistanceValue(stats.totalMeters),
            }
            : null,
        walked ? { key: "target", label: "Of the shared target", value: `${targetPercent}%` } : null,
        stats.participantCount > 0
            ? {
                key: "people",
                label: "Colleagues took part",
                value: stats.participantCount.toLocaleString(),
            }
            : null,
        stats.routeFinisherCount > 0
            ? {
                key: "finishers",
                label: "Finished the route",
                value: stats.routeFinisherCount.toLocaleString(),
            }
            : null,
        { key: "days", label: "Days it ran", value: stats.durationDays.toLocaleString() },
    ];
    return tiles.filter((tile) => tile !== null);
}
//# sourceMappingURL=past-challenge-overview.js.map